import { Link } from "react-router-dom";
import { TrendingUp, Globe, Zap, DollarSign } from "lucide-react";

const highlights = [
  { icon: TrendingUp, num: "$4.8T", label: "Cloud market by 2028", text: "The cloud computing market is the fastest-growing sector in technology." },
  { icon: Globe, num: "300+", label: "Edge locations", text: "Global infrastructure rivaling the incumbents — built from scratch for the modern web." },
  { icon: Zap, num: "10x", label: "Faster deploys", text: "Our architecture enables sub-second deployments, 10x faster than legacy platforms." },
  { icon: DollarSign, num: "40%", label: "Cost reduction", text: "Customers save an average of 40% compared to AWS, GCP, and Azure." },
];

const timeline = [
  { year: "2024 Q1", event: "Founded. Raised pre-seed from top-tier angels." },
  { year: "2024 Q3", event: "Core platform built. First 50 beta users onboarded." },
  { year: "2025 Q1", event: "300+ edge locations live. Waitlist passes 5,000 developers." },
  { year: "2025 Q3", event: "Public launch: Eamico Studio + Eamico Cloud." },
  { year: "2026", event: "Enterprise tier. SOC 2 certification. Series A." },
];

const InvestorsPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="investors-page">
      {/* Hero */}
      <div className="bg-[#f8f9fa] border-b border-[#e8eaed]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <div className="max-w-3xl">
            <p className="text-[12px] font-bold text-[#ff9a4d] tracking-wide uppercase mb-4">Investors</p>
            <h1 className="text-[32px] sm:text-[40px] lg:text-[48px] font-normal text-[#202124] leading-tight" data-testid="investors-title">
              The next great cloud platform starts here
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#5f6368] leading-relaxed max-w-2xl">
              Eamico is building the developer-first cloud platform that makes deploying code as easy as writing it. We're capturing a massive market with a fundamentally better product.
            </p>
            <div className="mt-8">
              <Link to="/enterprise" className="inline-flex items-center px-6 py-3 bg-[#ff9a4d] hover:bg-[#e8872e] text-white text-[14px] font-medium rounded-md transition-colors shadow-sm">
                Get in touch
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Market highlights */}
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
        <h2 className="text-[28px] sm:text-[34px] font-normal text-[#202124] mb-12">The opportunity</h2>
        <div className="grid sm:grid-cols-2 gap-6 lg:gap-8" data-testid="investors-highlights">
          {highlights.map((h, i) => {
            const Icon = h.icon;
            return (
              <div key={i} className="p-8 rounded-xl border border-[#e8eaed]" data-testid={`highlight-${i}`}>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#fff4eb] flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#ff9a4d]" />
                  </div>
                  <span className="text-[32px] font-normal text-[#202124]">{h.num}</span>
                </div>
                <h3 className="text-[16px] font-medium text-[#202124] mb-1">{h.label}</h3>
                <p className="text-[14px] text-[#5f6368] leading-relaxed">{h.text}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-[#f8f9fa] border-t border-[#e8eaed]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <h2 className="text-[28px] sm:text-[34px] font-normal text-[#202124] mb-12">Roadmap</h2>
          <div className="max-w-2xl space-y-0" data-testid="investors-timeline">
            {timeline.map((t, i) => (
              <div key={i} className="flex gap-6" data-testid={`timeline-${i}`}>
                <div className="flex flex-col items-center">
                  <div className={`w-3 h-3 rounded-full shrink-0 ${i <= 2 ? "bg-[#ff9a4d]" : "bg-[#dadce0]"}`} />
                  {i < timeline.length - 1 && <div className="w-px h-full bg-[#e8eaed] min-h-[48px]" />}
                </div>
                <div className="pb-8">
                  <p className="text-[13px] font-bold text-[#ff9a4d] uppercase tracking-wide">{t.year}</p>
                  <p className="text-[15px] text-[#202124] mt-1">{t.event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="border-t border-[#e8eaed]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-16 sm:py-20 text-center">
          <h2 className="text-[28px] sm:text-[34px] font-normal text-[#202124]">Interested in partnering?</h2>
          <p className="mt-3 text-[15px] text-[#5f6368]">We'd love to hear from you.</p>
          <Link to="/enterprise" className="inline-flex items-center mt-6 px-6 py-3 bg-[#ff9a4d] hover:bg-[#e8872e] text-white text-[14px] font-medium rounded-md transition-colors shadow-sm">
            Contact us
          </Link>
        </div>
      </div>
    </div>
  );
};

export default InvestorsPage;
